/**
 * The composer.
 *
 * Two modes with a visible switch, and they post to different endpoints. In Note mode the
 * channel row is not styled out, it is not in the DOM at all, and the preview panel renders
 * nothing that could be mistaken for something a customer will read. That is the UI half of
 * the guarantee; the structural half is that the note endpoint has no channel field and the
 * email sender queries the replies table only.
 *
 * Send holds for sixty seconds by default with a visible countdown, because an email cannot
 * be unsent and a held one can be cancelled.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Toggle, buttonStyle } from '@/features/customers/temp-ui';
import { useAddInternalNote, useMacros, useSaveDraft, useSendReply } from './api';

export type ComposerMode = 'reply' | 'note';
export const HOLD_SECONDS = 60;
export const DRAFT_AUTOSAVE_MS = 5_000;

/**
 * What the customer would receive. Returns null in note mode, which is what the preview
 * panel renders, so an internal note can never appear in a customer visible preview.
 */
export function customerVisiblePreview(mode: ComposerMode, body: string): string | null {
  return mode === 'reply' ? body : null;
}

export function Composer({
  ticketRef,
  canSms,
  initialDraft,
}: {
  ticketRef: string;
  canSms: boolean;
  initialDraft: string;
}) {
  const [mode, setMode] = useState<ComposerMode>('reply');
  const [body, setBody] = useState(initialDraft);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [resolveOnSend, setResolveOnSend] = useState(true);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [macroId, setMacroId] = useState<string | null>(null);

  const macros = useMacros();
  const sendReply = useSendReply(ticketRef);
  const addNote = useAddInternalNote(ticketRef);
  const saveDraft = useSaveDraft(ticketRef);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (mode !== 'reply') return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => saveDraft.mutate(body), DRAFT_AUTOSAVE_MS);
    return () => {
      if (draftTimer.current) clearTimeout(draftTimer.current);
    };
    // saveDraft is a stable mutation object from TanStack Query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, mode]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1_000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const preview = useMemo(() => customerVisiblePreview(mode, body), [mode, body]);
  const noteMode = mode === 'note';

  return (
    <section
      aria-label="Composer"
      data-mode={mode}
      style={{
        border: noteMode ? '2px dashed var(--warn)' : 'var(--border-width) solid var(--border)',
        background: noteMode ? 'var(--warn-bg)' : 'var(--surface-1)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--card-padding)',
      }}
    >
      <div role="tablist" aria-label="Composer mode" style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'reply'}
          onClick={() => setMode('reply')}
          style={buttonStyle(mode === 'reply' ? 'primary' : 'ghost')}
        >
          Reply
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'note'}
          onClick={() => setMode('note')}
          style={buttonStyle(mode === 'note' ? 'primary' : 'ghost')}
        >
          Internal note
        </button>
      </div>

      {noteMode ? (
        <p className="caps-label" style={{ color: 'var(--warn)' }}>
          Internal. This never leaves the building.
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            aria-pressed={channel === 'email'}
            onClick={() => setChannel('email')}
            style={buttonStyle(channel === 'email' ? 'primary' : 'ghost')}
          >
            email
          </button>
          <button
            type="button"
            aria-pressed={channel === 'sms'}
            disabled={!canSms}
            title={canSms ? undefined : 'No phone on file, or the customer has not opted in to SMS.'}
            onClick={() => setChannel('sms')}
            style={buttonStyle(channel === 'sms' ? 'primary' : 'ghost')}
          >
            + SMS
          </button>
          <select
            aria-label="Macro"
            value={macroId ?? ''}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setMacroId(value || null);
              const macro = (macros.data?.items ?? []).find((m) => m.macro_id === value);
              if (macro) setBody(macro.body_email);
            }}
          >
            <option value="">Macro</option>
            {(macros.data?.items ?? []).map((macro) => (
              <option key={macro.macro_id} value={macro.macro_id}>
                {macro.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <textarea
        aria-label={noteMode ? 'Internal note' : 'Reply'}
        value={body}
        rows={5}
        onChange={(event) => setBody(event.currentTarget.value)}
        style={{
          display: 'block',
          width: '100%',
          marginTop: 'var(--space-3)',
          padding: 'var(--space-2)',
          borderRadius: 'var(--radius-input)',
          border: 'var(--border-width) solid var(--border)',
          background: 'var(--surface-2)',
          color: 'var(--text)',
        }}
      />

      <section aria-label="Customer visible preview" data-testid="customer-preview" style={{ marginTop: 'var(--space-3)' }}>
        <span className="caps-label" style={{ color: 'var(--text-faint)' }}>
          The customer will see
        </span>
        {preview === null ? (
          <p style={{ color: 'var(--text-faint)', margin: 0 }}>Nothing. An internal note is not sent.</p>
        ) : (
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{preview}</p>
        )}
      </section>

      <footer style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
        {noteMode ? (
          <button
            type="button"
            style={buttonStyle('primary')}
            disabled={body.trim() === '' || addNote.isPending}
            onClick={() =>
              addNote.mutate({ body }, { onSuccess: () => setBody('') })
            }
          >
            Add note
          </button>
        ) : (
          <>
            <Toggle checked={resolveOnSend} onChange={setResolveOnSend} label="Resolve on send" />
            <button
              type="button"
              style={buttonStyle('primary')}
              disabled={body.trim() === '' || sendReply.isPending}
              onClick={() => {
                sendReply.mutate(
                  {
                    body,
                    channel,
                    macro_id: macroId,
                    set_status: resolveOnSend ? 'resolved' : null,
                    hold_seconds: HOLD_SECONDS,
                    reason: null,
                  },
                  {
                    onSuccess: () => {
                      setCountdown(HOLD_SECONDS);
                      setBody('');
                    },
                  },
                );
              }}
            >
              Send (holds {HOLD_SECONDS}s)
            </button>
            {countdown === null ? null : (
              <span role="status" style={{ color: 'var(--warn)' }}>
                Sending in {countdown}s
              </span>
            )}
          </>
        )}
      </footer>
    </section>
  );
}
